import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ref } from 'vue'
import { getConfig } from '@/config'

/** Session expiry timestamp (Unix seconds) — updated from X-Session-Expires header */
export const sessionExpiresAt = ref<number | null>(null)

// Create axios instance with default baseURL (updated after config loads via initClient)
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store',
    Pragma: 'no-cache',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

/** Call after loadConfig() resolves to set the correct baseURL */
export function initClient() {
  apiClient.defaults.baseURL = getConfig().apiBaseUrl
}

// --- Refresh-queue state ---
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: AxiosResponse) => void
  reject: (reason: unknown) => void
  config: InternalAxiosRequestConfig
}> = []

function processQueue(error: unknown) {
  for (const entry of failedQueue) {
    if (error) {
      entry.reject(SESSION_EXPIRED)
    } else {
      apiClient.request(entry.config).then(entry.resolve, entry.reject)
    }
  }
  failedQueue = []
}

/** Sentinel error — components can check `error.isSessionExpired` to silently ignore */
class SessionExpiredError extends Error {
  isSessionExpired = true
  constructor() {
    super('Session expired')
  }
}

function dispatchSessionExpired() {
  console.debug('[client] dispatching session:expired')
  window.dispatchEvent(new CustomEvent('session:expired'))
}

const SESSION_EXPIRED = new SessionExpiredError()

// --- Response interceptor ---
apiClient.interceptors.response.use(
  (response) => {
    // Update session expiry from header on every successful response
    const expiresHeader = response.headers['x-session-expires']
    if (expiresHeader) {
      sessionExpiresAt.value = Number(expiresHeader)
      console.debug(
        '[client] session expires at:',
        new Date(Number(expiresHeader) * 1000).toLocaleTimeString(),
      )
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean }

    if (error.response?.status === 401) {
      // If the refresh endpoint itself failed, session is truly expired
      const requestUrl = originalRequest?.url || ''
      if (requestUrl.includes('/auth/refresh')) {
        dispatchSessionExpired()
        return Promise.reject(SESSION_EXPIRED)
      }

      // If already retried once, give up
      if (originalRequest?._retried) {
        dispatchSessionExpired()
        return Promise.reject(SESSION_EXPIRED)
      }

      // If another request is already refreshing, queue this one
      if (isRefreshing) {
        return new Promise<AxiosResponse>((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest })
        })
      }

      // Attempt refresh
      isRefreshing = true
      originalRequest._retried = true

      try {
        const baseUrl = apiClient.defaults.baseURL || ''
        const refreshResponse = await axios.post(baseUrl + '/auth/refresh', null, {
          withCredentials: true,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })

        // Update session expiry from refresh response
        const expiresHeader = refreshResponse.headers['x-session-expires']
        if (expiresHeader) {
          sessionExpiresAt.value = Number(expiresHeader)
        }

        isRefreshing = false
        processQueue(null)

        // Retry the original request
        return apiClient.request(originalRequest)
      } catch (refreshError) {
        isRefreshing = false
        processQueue(refreshError)
        dispatchSessionExpired()
        return Promise.reject(SESSION_EXPIRED)
      }
    }

    if (error.response?.status === 403) {
      console.error('Insufficient permissions')
    }

    if (error.response?.status === 500) {
      console.error('Server error:', error.response.data)
    }

    return Promise.reject(error)
  },
)

export default apiClient
